import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";
import { useMemo } from "react";
import { useSubscription } from "react-relay";
import { GraphQLSubscriptionConfig } from "relay-runtime";

import { teeSheetAgendaLastUpdatedAtom } from "../../../atoms";
import { useAgendaTeeSheetSlotsChangedSubscription as AgendaTeeSheetSlotsChangedSubscription } from "./__generated__/useAgendaTeeSheetSlotsChangedSubscription.graphql";

export const useAgendaTeeSheetSlotsChangedSubscription = (args: {
  agendaId: string;
  courseIds: string[];
  date: string;
  membershipTypeId: string;
  isCustomer?: boolean;
  requestConfigs: boolean;
  onNext?: () => void;
}) => {
  const {
    agendaId,
    courseIds,
    date,
    isCustomer = false,
    membershipTypeId,
    onNext,
    requestConfigs,
  } = args;
  const setResponse = useSetAtom(teeSheetAgendaLastUpdatedAtom);
  const config: GraphQLSubscriptionConfig<AgendaTeeSheetSlotsChangedSubscription> =
    useMemo(() => {
      return {
        variables: {
          membershipTypeId,
          agendaID: agendaId,
          courseIds,
          date,
          isCustomer,
          requestConfigs,
        },
        subscription: AgendaTeeSheetSlotsChangedSubscriptionString,
        onNext: (response) => {
          if (response?.agendaSlotsChanged?.agenda) {
            setResponse(response);
            onNext?.();
          }
        },
      };
    }, [
      membershipTypeId,
      agendaId,
      courseIds,
      date,
      isCustomer,
      onNext,
      requestConfigs,
      setResponse,
    ]);
  useSubscription<AgendaTeeSheetSlotsChangedSubscription>(config);
};

// Both the initial query and this subscription read permissions through the
// facility once. Slot fragments only request slot-specific fields.
// The remaining selections mirror TimeSlotListInternal so subscription updates
// refresh the fields consumed by the tee sheet.
const AgendaTeeSheetSlotsChangedSubscriptionString = graphql`
  subscription useAgendaTeeSheetSlotsChangedSubscription(
    $membershipTypeId: ID!
    $agendaID: ID!
    $courseIds: [ID!]
    $isCustomer: Boolean!
    $requestConfigs: Boolean!
  ) {
    agendaSlotsChanged(agendaIds: [$agendaID]) {
      agenda {
        facility {
          id
          ...getFacilityMemberPermissions_facility
        }
        blockingRestrictions: bookingRules(
          type: [RESTRICTION]
          bookingType: [STANDARD]
          isBlocking: true
          courseIds: $courseIds
          memberVisibility: [RESTRICTION_ONLY, SLOTS_AND_RESTRICTION, NONE]
        ) {
          edges {
            liftAt
            node {
              id
              name
              displayName
              ... on BookingRestrictionRule {
                memberMessage
                visibilityColor
                memberVisibility
                liftBookingRuleWindow {
                  daysAhead
                  time
                }
                slotCriteria {
                  allDay
                  startAt
                  endAt
                  courses {
                    count
                    edges {
                      node {
                        id
                        name
                      }
                    }
                  }
                }
              }
            }
          }
        }
        infoRestrictions: bookingRules(
          type: [RESTRICTION]
          memberVisibility: [RESTRICTION_ONLY, SLOTS_AND_RESTRICTION]
          courseIds: $courseIds
        ) {
          edges {
            liftAt
            node {
              id
              name
              displayName
              ... on BookingRestrictionRule {
                memberMessage
                visibilityColor
                memberVisibility
                slotCriteria {
                  allDay
                  startAt
                  endAt
                  courses {
                    count
                    edges {
                      node {
                        name
                        id
                      }
                    }
                  }
                }
              }
            }
          }
        }
        requestRestrictions: bookingRules(type: [REQUEST])
          @skip(if: $requestConfigs) {
          edges {
            liftAt
            node {
              id
              name
              displayName
              ... on RequestRule {
                memberMessage
                liftBookingRuleWindow {
                  daysAhead
                  time
                }
                slotCriteria {
                  allDay
                  startAt
                  endAt
                  courses {
                    edges {
                      node {
                        id
                      }
                    }
                  }
                }
              }
            }
          }
        }
        configuredRequestRestrictions: requestWindowBookingRules(
          type: [REQUEST]
        ) @include(if: $requestConfigs) {
          edges {
            liftAt
            requestOpens
            requestCloses
            node {
              id
              name
              displayName
              ... on RequestRule {
                memberMessage
                memberVisibility
                visibilityColor
                requestConfig {
                  id
                }
                liftBookingRuleWindow {
                  daysAhead
                  time
                }
                slotCriteria {
                  allDay
                  startAt
                  endAt
                  courses {
                    edges {
                      node {
                        id
                      }
                    }
                  }
                }
              }
            }
          }
        }
        timeSlots(first: 1000, courseIds: $courseIds) {
          edges {
            node {
              ...TimeSlotRow_Timeslot
                @arguments(membershipTypeId: $membershipTypeId)
              id
              time
              availability
              block {
                id
              }
              course {
                id
                name
              }
              eventSlot {
                eventSession {
                  id
                }
              }
              bookings {
                edges {
                  node {
                    writeAccess
                    rounds(noShowStatus: [PRESENT]) {
                      edges {
                        node {
                          __typename
                          player {
                            id
                            firstName
                            lastName
                            __typename
                          }
                        }
                      }
                    }
                  }
                }
              }
              hasHiddenBlockingRestriction
              requestRestrictions: bookingRules(type: [REQUEST])
                @skip(if: $requestConfigs) {
                edges {
                  liftAt
                  node {
                    id
                    name
                    displayName
                    ... on RequestRule {
                      memberMessage
                      liftBookingRuleWindow {
                        daysAhead
                        time
                      }
                      slotCriteria {
                        allDay
                        startAt
                        endAt
                      }
                    }
                  }
                }
              }
              configuredRequestRestrictions: requestWindowBookingRules(
                type: [REQUEST]
              ) @include(if: $requestConfigs) {
                edges {
                  liftAt
                  requestOpens
                  requestCloses
                  node {
                    id
                    name
                    displayName
                    ... on RequestRule {
                      memberMessage
                      memberVisibility
                      visibilityColor
                      requestConfig {
                        id
                      }
                      liftBookingRuleWindow {
                        daysAhead
                        time
                      }
                      slotCriteria {
                        allDay
                        startAt
                        endAt
                      }
                    }
                  }
                }
              }
              waitlistRestrictions: bookingRules(type: [WAITLIST]) {
                edges {
                  node {
                    id
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
